import { useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WAREHOUSE_ROUTES } from 'constants/routes';
import type { InboundFilters, OutboundFilters } from 'models/Warehouse';

export const useWarehouseOperations = () => {
  const [searchValue, setSearchValue] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [inboundFilters, setInboundFilters] = useState<InboundFilters>({});
  const [outboundFilters, setOutboundFilters] = useState<OutboundFilters>({});
  const location = useLocation();
  const navigate = useNavigate();

  const isInbound = useMemo(() => location.pathname.includes('/inbound'), [location.pathname]);
  const isOutbound = useMemo(() => location.pathname.includes('/outbound'), [location.pathname]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleFilterClick = useCallback(() => {
    setFilterDrawerOpen(true);
  }, []);

  const handleFilterClose = useCallback(() => {
    setFilterDrawerOpen(false);
  }, []);

  const handleInboundFiltersApply = useCallback((filters: InboundFilters) => {
    setInboundFilters(filters);
    setFilterDrawerOpen(false);
  }, []);

  const handleOutboundFiltersApply = useCallback((filters: OutboundFilters) => {
    setOutboundFilters(filters);
    setFilterDrawerOpen(false);
  }, []);

  const handleNavigateToInbound = useCallback(() => {
    // Reset search and filters before navigation
    setSearchValue('');
    setInboundFilters({});
    setOutboundFilters({});
    navigate(WAREHOUSE_ROUTES.inboundPackages);
  }, [navigate]);

  const handleNavigateToOutbound = useCallback(() => {
    // Reset search and filters before navigation
    setSearchValue('');
    setInboundFilters({});
    setOutboundFilters({});
    navigate(WAREHOUSE_ROUTES.outboundPackages);
  }, [navigate]);

  return {
    searchValue,
    filterDrawerOpen,
    inboundFilters,
    outboundFilters,
    isInbound,
    isOutbound,
    handleSearch,
    handleFilterClick,
    handleFilterClose,
    handleInboundFiltersApply,
    handleOutboundFiltersApply,
    handleNavigateToInbound,
    handleNavigateToOutbound,
  };
};
